/** Normalize various API envelope shapes to an array of rows. */
export function extractListFromResponse(body) {
  if (!body) return [];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  if (Array.isArray(body.data?.data)) return body.data.data;
  return [];
}

/** Read pagination meta from common API shapes (1-based page when present). */
export function extractPaginationMeta(body, fallbackPage, fallbackLimit) {
  if (!body || typeof body !== "object") {
    return {
      page: fallbackPage || 1,
      total: 0,
      limit: fallbackLimit || 10,
    };
  }
  const meta = body.meta || {};
  const page =
    Number(
      meta.page ??
        meta.currentPage ??
        body.page ??
        body.currentPage ??
        fallbackPage
    ) || fallbackPage ||
    1;
  const total = Number(
    meta.total ?? meta.totalCount ?? body.total ?? body.totalCount ?? 0
  );
  const limit =
    Number(
      meta.limit ??
        meta.pageSize ??
        body.limit ??
        body.pageSize ??
        fallbackLimit
    ) || fallbackLimit ||
    10;
  return { page, total, limit };
}

export function formatPayoutMoney(val) {
  if (val === null || val === undefined || val === "") return "—";
  if (typeof val === "number" && !Number.isNaN(val)) {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(val);
  }
  const s = String(val).trim();
  if (s.startsWith("$")) return s;
  const n = parseFloat(s.replace(/,/g, ""));
  if (Number.isNaN(n)) return s;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(n);
}

export function isPayoutCompleteStatus(status) {
  const s = (status || "").toString().toLowerCase().trim();
  return ["complete", "completed", "success", "paid", "succeeded"].includes(s);
}

export function isPayoutFailedStatus(status) {
  const s = (status || "").toString().toLowerCase().trim();
  return [
    "failed",
    "failure",
    "error",
    "rejected",
    "declined",
    "canceled",
    "cancelled",
  ].includes(s);
}

export function formatPayoutStatusLabel(status) {
  const s = (status || "").toString().trim();
  if (!s) return "—";
  const lower = s.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function formatAddressLine(addr) {
  if (!addr || typeof addr !== "object") return "";
  const parts = [
    addr.house_no,
    addr.street_address,
    addr.landmark,
    addr.city,
    addr.state,
    addr.zipcode,
  ].filter(Boolean);
  return parts.join(", ");
}

function maskStripeAccountId(acct) {
  if (!acct || typeof acct !== "string") return null;
  const t = acct.trim();
  if (t.length <= 10) return t;
  return `${t.slice(0, 7)}…${t.slice(-4)}`;
}

/**
 * Build partner panel state from `/payment/payout` list response (`store` / `deliveryPerson` on envelope).
 */
export function mapPartnerDetailsFromPayoutResponse(
  body,
  payoutsType,
  nameFallback
) {
  if (payoutsType === "driver_payout") {
    const dp = body?.deliveryPerson;
    if (!dp || typeof dp !== "object") return null;
    const u = dp.user;
    const fallback = nameFallback || "Partner";
    return {
      kind: "driver",
      title: u?.name || fallback,
      profileImageUrl: u?.profile_img_url || null,
      active: !!u?.user_status,
      rows: [
        ["Email", u?.email],
        ["Mobile", u?.phone_number],
        ["Vehicle type", dp.vehicle_type],
        ["Vehicle number", dp.vehicle_number],
        ["Payout frequency", dp.payout_frequency],
        ["Stripe", maskStripeAccountId(dp.stripeAccountId)],
        ["Payout freeze", dp.payoutFreeze ? "Yes" : "No"],
      ].filter(([, v]) => v),
    };
  }

  const s = body?.store;
  if (!s || typeof s !== "object") return null;

  const addr = s.storeAddresses?.[0];
  const docs = Array.isArray(s.uploadDoc) ? s.uploadDoc : [];
  const thumbUrl = docs.find((d) => d.name === "shop_image")?.fileUrl || null;

  const serviceAreas = Array.isArray(s.serviceAreas) ? s.serviceAreas : [];
  const routings = [
    ...new Set(serviceAreas.map((a) => a?.routingNumber).filter(Boolean)),
  ].join(", ");
  const coverageLabel =
    serviceAreas.length > 0
      ? `${serviceAreas.length} zone(s)${routings ? ` · ${routings}` : ""}`
      : null;

  const stripeLabel = maskStripeAccountId(s.stripeAccountId);

  const badges = [
    {
      label: s.storeStatus ? "Active" : "Inactive",
      variant: s.storeStatus ? "success" : "neutral",
    },
  ];
  if (s.preferred) {
    badges.push({ label: "Preferred", variant: "primary" });
  }
  if (s.payoutFreeze) {
    badges.push({ label: "Payout frozen", variant: "warning" });
  }

  const owner = s.user;
  const overviewRows = [
    ["Phone", s.store_phone],
    ["Address", formatAddressLine(addr)],
    ["License", s.licenceNumber],
    ["Service coverage", coverageLabel],
    ["Payout frequency", s.payout_frequency],
    ["Stripe", stripeLabel],
    ["Payout freeze", s.payoutFreeze ? "Yes" : "No"],
  ].filter(([, v]) => v);

  return {
    kind: "store",
    title: s.storeName || nameFallback || "Store",
    thumbUrl,
    badges,
    owner: owner
      ? {
          name: owner.name,
          email: owner.email,
          phone: owner.phone_number,
          active: owner.user_status,
        }
      : null,
    overviewRows,
  };
}
